import React, { useMemo } from 'react';

import {
    setWidgetDevice,
    setWidgetLoader,
    setWidgetSource,
} from '@fiji/common/src/features/widgetManagement/widgetConfigurationSlice';

import {
    setMapSelectedNodes,
    setPredefinedMapConfiguration,
} from '@fiji/common/src/features/widgetManagement/mapWidgetSlice';

import {
    setPredefinedDeviceList,
    setDeviceListSelectedNodes,
} from '@fiji/common/src/features/widgetManagement/deviceListWidgetSlice';

import {
    setTimelineSelectedNodes,
    setPredefinedTimelineConfiguration,
} from '@fiji/common/src/features/widgetManagement/timelineWidgetSlice';

import {
    setScheduleSelectedNodes,
    setPredefinedScheduleConfiguration,
} from '@fiji/common/src/features/widgetManagement/scheduleWidgetSlice';

import {
    setPredefinedUpcoming,
    setUpcomingSelectedNodes,
} from '@fiji/common/src/features/widgetManagement/upcomingWidgetSlice';
import {
    setEnvironmentalStressMonitoringSelectedNodes,
    setPredefinedEnvironmentalStressMonitoring,
} from '@fiji/common/src/features/widgetManagement/environmentalStressMonitoringWidgetSlice';

import {
    setPredefinedComponent,
    setComponentSelectedNodes,
} from '@fiji/common/src/features/widgetManagement/componentWidgetSlice';

import {
    setMessageContent,
    setPageHeaderData,
    setAppHeaderSubTitle,
} from '@fiji/common/src/features/common/commonSlice';

import {
    useGetDeviceByProfileQuery,
    useGetMultiDeviceLoadsQuery,
    useGetSearchedDeviceOrGroupQuery,
} from '@fiji/common/src/features/deviceManagement/deviceApi';

import {
    setGroupListSelectedNodes,
    setPredefinedGroupListConfiguration,
} from '@fiji/common/src/features/widgetManagement/groupListWidgetSlice';

import {
    handleCleanMapPayload,
    handleCleanLoadsPayload,
    handleCleanTrendsPayload,
    handleCleanDefaultComponentPayload,
    handleCleanTimelineDeviceListAndGroupListPayload,
    handleCleanLoadGroupsPayload,
    filterMultiDeviceConfigByEntities,
} from '../utils/helpers';

import { selectedOrg } from '@fiji/common/src/features/orgManagement/orgSlice';
import { useTranslation } from 'react-i18next';
import { useNavigationBack } from 'hooks';
import { setPredefinedGauge } from '@fiji/common/src/features/widgetManagement/gaugeWidgetSlice';
import { setPredefinedChart } from '@fiji/common/src/features/widgetManagement/chartWidgetSlice';
import { setPredefinedTrends } from '@fiji/common/src/features/widgetManagement/trendsWidgetSlice';
import { setPredefinedMetrics } from '@fiji/common/src/features/widgetManagement/metricsWidgetSlice';
import { setPredefinedWeather } from '@fiji/common/src/features/widgetManagement/weatherWidgetSlice';
import { setDashboardWidgetIds } from '@fiji/common/src/features/dashboardManagement/dashboardSlice';
import { useLocation, useParams } from 'react-router-dom';
import { setPredefinedLoadTrends } from '@fiji/common/src/features/widgetManagement/loadTrendsWidgetSlice';
import { setPredefinedEnergyFlow } from '@fiji/common/src/features/widgetManagement/energyFlowWidgetSlice';
import { setPredefinedVirtualLoads } from '@fiji/common/src/features/widgetManagement/virtualLoadsWidgetSlice';
import { setPredefinedConfiguration } from '@fiji/common/src/features/widgetManagement/detailsWidgetSlice';
import { setPredefinedAggregatedTrends } from '@fiji/common/src/features/widgetManagement/aggregatedTrendsWidgetSlice';
import { useAppDispatch, useTypedSelector } from '@fiji/common/src/app/store';
import { setPredefinedConsumptionBreakdown } from '@fiji/common/src/features/widgetManagement/consumptionBreakdownWidgetSlice';
import { setLoadsNodes, setPredefinedLoads } from '@fiji/common/src/features/widgetManagement/loadsWidgetSlice';
import { setPredefinedCommandBarConfiguration } from '@fiji/common/src/features/widgetManagement/commandBarWidgetSlice';
import { setPredefinedScore, setScoreSelectedNodes } from '@fiji/common/src/features/widgetManagement/scoreWidgetSlice';
import { useGetWidgetByIdQuery, useUpdateWidgetMutation } from '@fiji/common/src/features/widgetManagement/widgetApi';
import { getWidgetTypeValidationConfig, isNullOrUndefined } from '@fiji/common/src/utils/helpers';
import { setPredefinedLoadGroups } from '@fiji/common/src/features/widgetManagement/loadGroupWidgetSlice';
import { setPredefinedLoadGroupTrends } from '@fiji/common/src/features/widgetManagement/loadGroupTrendsSlice';
import { useWidgetSelectedEntities } from './useWidgetSelectedEntities';

const PREDEFINED_HANDLER_MAP: Record<string, (data: any) => any> = {
    gauge: setPredefinedGauge,
    charts: setPredefinedChart,
    trends: setPredefinedTrends,
    virtual_loads: setPredefinedVirtualLoads,
    group_list: setPredefinedGroupListConfiguration,
    map: setPredefinedMapConfiguration,
    device_list: setPredefinedDeviceList,
    timeline: setPredefinedTimelineConfiguration,
    command_bar: setPredefinedCommandBarConfiguration,
    schedule: setPredefinedScheduleConfiguration,
    loads: setPredefinedLoads,
    load_groups: setPredefinedLoadGroups,
    load_group_trends: setPredefinedLoadGroupTrends,
    score: setPredefinedScore,
    upcoming: setPredefinedUpcoming,
    environmental_stress_monitoring: setPredefinedEnvironmentalStressMonitoring,
    components: setPredefinedComponent,
    load_trends: setPredefinedLoadTrends,
    consumption_breakdown: setPredefinedConsumptionBreakdown,
    metrics: setPredefinedMetrics,
    energy_flow: setPredefinedEnergyFlow,
    weather: setPredefinedWeather,
    aggregated_trends: setPredefinedAggregatedTrends,
};

// Widget types that support display preferences (header, count, last updated)
const DISPLAY_PREFERENCE_WIDGET_TYPES = ['details', 'settings', 'properties'];

type DisplayPreferences = {
    showWidgetTitleBar: boolean;
    isTotalCountVisible: boolean;
    showLastUpdateDate: boolean;
    viewAllButton: boolean;
};

type ReturnProps = {
    handleSaveButton: (args?: any) => Promise<void>;
    updateLoader: boolean;
    isLoading: boolean;
    deviceError: boolean;
    renderSx: any;
    details: { widgetDetails: any; deviceDetails: any };
    handleCloseButton: () => void;
    predefinedHandlers: (args: any) => any;
};

export const useWidgetConfigHandlers = (customSuccessMessage?: string): ReturnProps => {
    const { t } = useTranslation();
    const { widgetId } = useParams();
    const { state } = useLocation();
    const dispatch = useAppDispatch();
    const currentOrg = useTypedSelector(selectedOrg);
    const navigationBack = useNavigationBack();
    const widgetData = useTypedSelector((data: any) => data.widgetConfiguration.data);
    const multiDevice = useTypedSelector((data: any) => data.widgetConfiguration.multiDevice);
    const selectedNode = useTypedSelector((data: any) => data?.widgetConfiguration?.device);
    const source = useTypedSelector((data: any) => data.widgetConfiguration.source);
    const { buildSelectedEntities } = useWidgetSelectedEntities();
    const displayPreferences = useTypedSelector((reduxState): DisplayPreferences => {
        const details = reduxState.details;
        return {
            showWidgetTitleBar: details?.showWidgetTitleBar ?? true,
            isTotalCountVisible: details?.isTotalCountVisible ?? true,
            showLastUpdateDate: details?.showLastUpdateDate ?? true,
            viewAllButton: details?.viewAllButton ?? true,
        };
    });

    const saveAsCount = useTypedSelector((data: any) => data['widgetConfiguration']['saveAsCount']);
    const handleNavigateBack = (): void => {
        const locationPathName = window.location.pathname;
        // For editWidget pages, only go back 1 step regardless of saveAsCount
        // This ensures: editWidget -> editDashboard -> summary flow
        if (locationPathName?.includes('/editWidget')) {
            navigationBack(1);
        } else if (locationPathName?.includes('/editDashboard') && saveAsCount > 1) {
            // For editDashboard, use saveAsCount to navigate back to summary (skip intermediate editWidget)
            navigationBack(saveAsCount);
        } else if (saveAsCount > 1) {
            navigationBack(saveAsCount);
        } else {
            navigationBack();
        }
    };
    const {
        data: widgetDetails,
        isLoading,
        isFetching,
        isError,
    }: any = useGetWidgetByIdQuery(widgetId, { skip: !widgetId || !currentOrg?.id });

    const {
        data: deviceDetails,
        isLoading: deviceDetailsLoading,
        isFetching: deviceDetailsFetching,
        isError: deviceError,
    } = useGetDeviceByProfileQuery(source?.id, {
        skip: !source?.id,
    });

    const [updateWidget, { isLoading: updateLoader }] = useUpdateWidgetMutation();

    const handleMultiLoadsPayload = (): any => {
        const payload: any = [];

        if (widgetDetails?.data?.config?.selectedNodes?.length) {
            widgetDetails?.data?.config?.selectedNodes?.forEach((item: any) => {
                const selectedIndex = payload?.findIndex((subItem: any) => subItem.deviceId === item.deviceId);
                const selectedItem = payload?.find((subItem: any) => subItem.deviceId === item.deviceId);
                if (selectedItem) {
                    payload?.[selectedIndex]?.loadId?.push(item.loadId);
                } else {
                    payload?.push({ deviceId: item.deviceId, loadId: [item.loadId] });
                }
            });
        }
        return payload;
    };

    const { data: searchedData } = useGetSearchedDeviceOrGroupQuery(
        {
            entityIds: widgetDetails?.data?.config?.filters
                ? Object?.values(widgetDetails?.data?.config?.filters)?.flat(Infinity)
                : [],
        },
        {
            skip: !Object.keys(widgetDetails?.data?.config?.filters ?? {})?.length,
        }
    );

    const { data: loads } = useGetMultiDeviceLoadsQuery(handleMultiLoadsPayload(), {
        skip: !widgetDetails?.data?.config?.selectedNodes?.length || widgetDetails?.data?.widgetType?.id !== 'loads',
        refetchOnMountOrArgChange: true,
    });

    React.useEffect(() => {
        if (widgetDetails && widgetDetails?.data?.widgetType?.id === 'loads' && loads?.data?.response?.length) {
            const newNodesArr: any = [];
            loads?.data?.response?.forEach((deviceConfig: any) => {
                deviceConfig?.configurationList?.forEach((load: any) => {
                    newNodesArr.push({
                        loadId: load?.loadId,
                        id: `${load?.loadId}${deviceConfig?.deviceId}`?.replace(/-/g, '_'),
                        name: load?.loadName,
                        type: 'load',
                        groupPath: deviceConfig?.groupPath,
                        deviceId: deviceConfig?.deviceId,
                        icon: {
                            web: {
                                family: 'loads',
                                loadNumber: parseInt(load?.loadNumber ?? 0) + 1,
                                sx: {
                                    fontSize: '14px',
                                    fontWeight: '600',
                                    backgroundColor: '#727E84',
                                    color: '#fff',
                                    width: '18px',
                                    height: '18px',
                                    borderRadius: '4px',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                },
                            },
                        },
                    });
                });
            });
            dispatch(setLoadsNodes({ key: 'selectedNodes', value: newNodesArr }));
        }
    }, [loads, widgetDetails]);

    React.useEffect(() => {
        if (widgetData && searchedData?.data?.records && !widgetData?.selectedNodes) {
            switch (widgetDetails?.data?.widgetType?.id) {
                case 'group_list':
                    dispatch(setGroupListSelectedNodes({ key: 'selectedNodes', value: searchedData?.data?.records }));
                    break;

                case 'device_list':
                    dispatch(setDeviceListSelectedNodes({ key: 'selectedNodes', value: searchedData?.data?.records }));
                    break;
                case 'map':
                    dispatch(setMapSelectedNodes({ key: 'selectedNodes', value: searchedData?.data?.records }));
                    break;

                case 'timeline':
                    dispatch(setTimelineSelectedNodes({ key: 'selectedNodes', value: searchedData?.data?.records }));
                    break;
                case 'virtual_loads':
                    dispatch(setTimelineSelectedNodes({ key: 'selectedNodes', value: searchedData?.data?.records }));
                    break;
                case 'upcoming':
                    dispatch(setUpcomingSelectedNodes({ key: 'selectedNodes', value: searchedData?.data?.records }));
                    break;
                case 'environmental_stress_monitoring':
                    dispatch(
                        setEnvironmentalStressMonitoringSelectedNodes({
                            key: 'selectedNodes',
                            value: searchedData?.data?.records,
                        })
                    );
                    break;
                case 'score':
                    dispatch(setScoreSelectedNodes({ key: 'selectedNodes', value: searchedData?.data?.records }));
                    break;
                case 'components':
                    dispatch(setComponentSelectedNodes({ key: 'selectedNodes', value: searchedData?.data?.records }));
                    break;
                case 'schedule':
                    dispatch(setScheduleSelectedNodes({ key: 'selectedNodes', value: searchedData?.data?.records }));
                    break;
                default:
                    break;
            }
        }
    }, [searchedData, widgetData]);

    React.useEffect(() => {
        if (widgetDetails?.data?.sourceId && !widgetDetails?.data?.multiDevice && !widgetData?.multiDevice && !source) {
            dispatch(
                setWidgetSource({
                    id: widgetDetails?.data?.sourceId,
                    name: widgetDetails?.data?.sourceName,
                    categoryId: widgetDetails?.data?.categoryId,
                })
            );
        }
    }, [widgetDetails, widgetData?.multiDevice, source]);

    React.useEffect(() => {
        if (widgetDetails) {
            dispatch(setPageHeaderData({ title: widgetDetails?.data?.name }));
            dispatch(setAppHeaderSubTitle(t('WIDGETS:WIDGETS_BUILDER')));
        }
    }, [widgetDetails]);

    React.useEffect(() => {
        // Widget slices clear their preview data on setWidgetDevice, so only dispatch on a real
        // device change — re-dispatching the same device blanks the preview until a remount.
        if (deviceDetails?.data && deviceDetails.data?.id !== selectedNode?.id) {
            dispatch(setWidgetDevice({ data: deviceDetails?.data, reset: false }));
        }
    }, [deviceDetails, selectedNode?.id]);

    React.useEffect(() => {
        if (isError) {
            dispatch(setWidgetLoader(false));
        }
    }, [isError]);

    async function handleSaveButton(cancelCallback?: any): Promise<void> {
        const configClone: any = JSON.parse(JSON.stringify(widgetData));
        switch (widgetDetails?.data?.widgetType?.id) {
            case 'timeline':
            case 'device_list':
            case 'group_list':
            case 'schedule':
                handleCleanTimelineDeviceListAndGroupListPayload(configClone);
                break;
            case 'map':
                handleCleanMapPayload(configClone);
                break;

            case 'gauge':
                delete configClone['data']['value'];
                break;
            case 'properties':
            case 'settings':
            case 'details':
                handleCleanDefaultComponentPayload(configClone);
                break;
            case 'charts':
                configClone['secondary']?.forEach((item: any) => {
                    delete item.value;
                });
                break;
            case 'trends':
                handleCleanTrendsPayload(configClone);
                break;
            case 'load_groups':
            case 'load_group_trends':
                handleCleanLoadGroupsPayload(configClone);
                break;
            case 'loads':
            case 'virtual_loads':
                handleCleanLoadsPayload(configClone);
                break;
            case 'score':
            case 'upcoming':
            case 'environmental_stress_monitoring':
            case 'components':
                handleCleanTimelineDeviceListAndGroupListPayload(configClone);
                break;
            case 'energy_flow':
                configClone['assets'] = configClone['assets']?.map((item: any) => ({
                    id: item.id,
                    deviceTypeId: item.deviceTypeId,
                    name: item.name,
                }));
                break;
            case 'metrics':
            case 'consumption_breakdown':
            case 'load_trends':
            case 'aggregated_trends':
                configClone['assets'] = configClone['assets']?.map((item: any) => ({
                    id: item?.id,
                    deviceTypeId: item?.deviceTypeId,
                    name: item?.name,
                }));
                break;
            case 'weather':
                configClone['assets'] = {
                    parentId: configClone['assets']?.parentId,
                    id: configClone['assets']?.id,
                    name: configClone['assets']?.name,
                    lat: configClone['assets']?.lat,
                    lng: configClone['assets']?.lng,
                };
                break;
            default:
                break;
        }

        const widgetTypeId = widgetDetails?.data?.widgetType?.id;
        if (widgetTypeId === 'timeline' || widgetTypeId === 'device_list') {
            delete configClone['secondary'];
            delete configClone['statusCount'];
        }

        // Add display preferences for Details, Settings, and Properties widgets
        if (DISPLAY_PREFERENCE_WIDGET_TYPES.includes(widgetTypeId)) {
            configClone.showWidgetTitleBar =
                displayPreferences.showWidgetTitleBar ?? configClone.showWidgetTitleBar ?? true;
            configClone.isTotalCountVisible =
                displayPreferences.isTotalCountVisible ?? configClone.isTotalCountVisible ?? true;
            configClone.showLastUpdateDate =
                displayPreferences.showLastUpdateDate ?? configClone.showLastUpdateDate ?? true;
            configClone.viewAllButton = displayPreferences.viewAllButton ?? configClone.viewAllButton ?? true;
        }
        const selectedEntities = buildSelectedEntities({
            widgetTypeId,
            cleanedConfig: widgetData?.config,
            rawConfig: configClone,
            sourceId: source?.id,
        });

        const { error }: any = await updateWidget({
            body: {
                config: configClone,
                selectedEntities,
                sourceId: source ? source?.id : null,
                sourceName: source ? source?.name : null,
            },
            widgetId: widgetId,
        });
        if (!error) {
            dispatch(
                setMessageContent({
                    isOpen: true,
                    message:
                        customSuccessMessage ?? `${widgetDetails?.data?.name ?? '-'} ${t('COMMON:HAS_BEEN_SAVED')}.`,
                })
            );
            if (!isNullOrUndefined(cancelCallback)) {
                cancelCallback();
            }
        }
    }

    function predefinedHandlers(data: any): void {
        const widgetTypeId = widgetDetails?.data?.widgetType?.id;
        // Filter primary/secondary against selectedWidgetEntities before Redux hydration (multiDevice only).
        const selectedWidgetEntities =
            widgetDetails?.data?.selectedEntities?.selectedWidgetEntities ?? data?.selectedWidgetEntities;
        const filteredData = filterMultiDeviceConfigByEntities(data, selectedWidgetEntities);
        const dataClone = JSON.parse(JSON.stringify(filteredData));

        if (widgetTypeId === 'details' || widgetTypeId === 'settings' || widgetTypeId === 'properties') {
            if (!multiDevice) {
                dataClone?.primary?.forEach((item: any) => {
                    item.deviceId = selectedNode?.id;
                });
                dataClone?.secondary?.forEach((item: any) => {
                    item.deviceId = selectedNode?.id;
                });
            }
            dispatch(setPredefinedConfiguration(dataClone));
            return;
        }

        const action = PREDEFINED_HANDLER_MAP[widgetTypeId];
        if (action) dispatch(action(filteredData));
    }

    function redirectionHandler(): void {
        if (state?.assignType) {
            dispatch(setDashboardWidgetIds(widgetDetails?.data?.id));
        }
        handleNavigateBack();
    }

    const renderSx = (): any => {
        if (getWidgetTypeValidationConfig(state?.typeId)['loaderType'] === 'progress') {
            return {
                position: 'relative',
                minHeight: '400px',
            };
        }
        return { position: 'relative' };
    };

    return {
        handleSaveButton,
        updateLoader,
        renderSx,
        deviceError,
        isLoading: useMemo(
            () => isLoading || isFetching || deviceDetailsFetching || deviceDetailsLoading,
            [isLoading || isFetching || deviceDetailsFetching || deviceDetailsLoading]
        ),
        details: useMemo(
            (): any => ({ widgetDetails: widgetDetails?.data, deviceDetails: deviceDetails?.data }),
            [widgetDetails?.data, deviceDetails?.data]
        ),
        handleCloseButton: redirectionHandler,
        predefinedHandlers,
    };
};
